import { Linkedin, Twitter, Send, Copy, Check } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { track } from "@/lib/analytics";

type Props = {
  /** Public URL being shared (portfolio, wrapped, etc.) */
  url: string;
  /** One-line headline used as share text */
  title: string;
  /** Optional surface label for analytics (e.g. "portfolio", "wrapped", "journey") */
  surface?: string;
  /** Compact = icon-only buttons. Default = icon + label. */
  compact?: boolean;
  /** Visual variant — "light" for dark backgrounds (Wrapped), "default" otherwise */
  tone?: "default" | "light";
};

export default function ShareBar({ url, title, surface = "portfolio", compact = false, tone = "default" }: Props) {
  const [copied, setCopied] = useState(false);

  const encodedUrl = encodeURIComponent(url);
  const encodedText = encodeURIComponent(title);

  const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
  const twitterUrl = `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedText}`;
  const whatsappUrl = `https://wa.me/?text=${encodedText}%20${encodedUrl}`;

  function trackShare(channel: string) {
    track("share_click", { surface, props: { channel, url } });
  }

  function openShare(channel: "linkedin" | "twitter" | "whatsapp") {
    const target = channel === "linkedin" ? linkedinUrl : channel === "twitter" ? twitterUrl : whatsappUrl;
    trackShare(channel);
    window.open(target, "_blank", "noopener,noreferrer,width=620,height=720");
  }

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      trackShare("copy");
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Couldn't copy link");
    }
  }

  const btnVariant = tone === "light" ? "secondary" : "outline";
  const size = compact ? "icon" : "sm";

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button size={size} variant={btnVariant} onClick={() => openShare("linkedin")} aria-label="Share to LinkedIn" className="gap-1.5">
        <Linkedin className="h-4 w-4 text-[#0A66C2]" />
        {!compact && <span>LinkedIn</span>}
      </Button>
      <Button size={size} variant={btnVariant} onClick={() => openShare("twitter")} aria-label="Share to X" className="gap-1.5">
        <Twitter className="h-4 w-4" />
        {!compact && <span>X</span>}
      </Button>
      <Button size={size} variant={btnVariant} onClick={() => openShare("whatsapp")} aria-label="Share on WhatsApp" className="gap-1.5">
        <Send className="h-4 w-4 text-[#25D366]" />
        {!compact && <span>WhatsApp</span>}
      </Button>
      <Button size={size} variant={btnVariant} onClick={copyLink} aria-label="Copy link" className="gap-1.5">
        {copied ? <Check className="h-4 w-4 text-primary" /> : <Copy className="h-4 w-4" />}
        {!compact && <span>{copied ? "Copied" : "Copy link"}</span>}
      </Button>
    </div>
  );
}
